import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { proyectoService, clienteService, cotizacionService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import ImageUploader from '../components/ImageUploader';
import '../styles/Proyectos.css';

const Proyectos = () => {
  const { esContratista, esCapataz } = useAuth();
  const navigate = useNavigate();
  const [proyectos, setProyectos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalAbierto, setModalAbierto] = useState(false);
  const [modalAvance, setModalAvance] = useState(false);
  const [proyectoSeleccionado, setProyectoSeleccionado] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [cotizaciones, setCotizaciones] = useState([]);
  const [filtroEstado, setFiltroEstado] = useState('');
  const [formData, setFormData] = useState({
    cliente_id: '',
    nombre: '',
    descripcion: '',
    direccion: '',
    fecha_inicio: '',
    fecha_fin_estimada: '',
    presupuesto_asignado: '',
    observaciones: ''
  });
  const [avanceData, setAvanceData] = useState({
    fecha: new Date().toISOString().split('T')[0],
    descripcion: '',
    porcentaje_avance: 0,
    observaciones: '',
    incidencias: '',
    imagenes: []
  });

  useEffect(() => {
    cargarProyectos();
    if (esContratista) {
      cargarClientes();
      cargarCotizaciones();
    }
  }, [filtroEstado]);

  const cargarProyectos = async () => {
    try {
      const params = filtroEstado ? { estado: filtroEstado } : {};
      const response = await proyectoService.getAll(params);
      setProyectos(response.data);
    } catch (error) {
      console.error('Error al cargar proyectos:', error);
    } finally {
      setLoading(false);
    }
  };

  const cargarClientes = async () => {
    try {
      const response = await clienteService.getAll();
      setClientes(response.data);
    } catch (error) {
      console.error('Error al cargar clientes:', error);
    }
  };

  const cargarCotizaciones = async () => {
    try {
      const response = await cotizacionService.getAll({ estado: 'aprobada', sin_proyecto: 'true' });
      setCotizaciones(response.data);
    } catch (error) {
      console.error('Error al cargar cotizaciones:', error);
    }
  };

  const abrirModal = () => {
    setFormData({
      cliente_id: '',
      nombre: '',
      descripcion: '',
      direccion: '',
      fecha_inicio: '',
      fecha_fin_estimada: '',
      presupuesto_asignado: '',
      observaciones: ''
    });
    setModalAbierto(true);
  };

  const cerrarModal = () => {
    setModalAbierto(false);
  };

  const abrirModalAvance = (proyecto) => {
    setProyectoSeleccionado(proyecto);
    setAvanceData({
      fecha: new Date().toISOString().split('T')[0],
      descripcion: '',
      porcentaje_avance: proyecto.porcentaje_avance || 0,
      observaciones: '',
      incidencias: '',
      imagenes: []
    });
    setModalAvance(true);
  };

  const cerrarModalAvance = () => {
    setModalAvance(false);
    setProyectoSeleccionado(null);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleAvanceChange = (e) => {
    setAvanceData({
      ...avanceData,
      [e.target.name]: e.target.value
    });
  };

  const handleImagenesAvance = (result) => {
    // result puede ser un objeto {url, filename} o array de objetos
    const imagenesArray = Array.isArray(result) ? result : [result];
    const imagenesObjetos = imagenesArray.map(item => ({
      url: typeof item === 'string' ? item : item.url,
      descripcion: ''
    }));

    setAvanceData({
      ...avanceData,
      imagenes: [...avanceData.imagenes, ...imagenesObjetos]
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await proyectoService.create(formData);
      cargarProyectos();
      cerrarModal();
      alert('Proyecto creado exitosamente');
    } catch (error) {
      console.error('Error al crear proyecto:', error);
      alert(error.response?.data?.error || 'Error al crear proyecto');
    }
  };

  const handleSubmitAvance = async (e) => {
    e.preventDefault();
    try {
      await proyectoService.registrarAvance({
        proyecto_id: proyectoSeleccionado.id,
        ...avanceData
      });
      cargarProyectos();
      cerrarModalAvance();
      alert('Avance registrado exitosamente');
    } catch (error) {
      console.error('Error al registrar avance:', error);
      alert(error.response?.data?.error || 'Error al registrar avance');
    }
  };

  const aprobarProyecto = async (proyecto_id) => {
    if (!window.confirm('¿Aprobar este proyecto? El capataz podrá verlo y registrar avances.')) return;

    try {
      await proyectoService.aprobar(proyecto_id);
      alert('Proyecto aprobado exitosamente');
      cargarProyectos();
    } catch (error) {
      console.error('Error al aprobar proyecto:', error);
      alert(error.response?.data?.error || 'Error al aprobar proyecto');
    }
  };

  const eliminarProyecto = async (proyecto_id, nombre_proyecto) => {
    if (!window.confirm(`¿Estás seguro de eliminar el proyecto "${nombre_proyecto}"?\n\nEsta acción no se puede deshacer.`)) return;

    try {
      await proyectoService.delete(proyecto_id);
      alert('Proyecto eliminado exitosamente');
      cargarProyectos();
      cargarCotizaciones(); // Recargar cotizaciones para que la cotización esté disponible nuevamente
    } catch (error) {
      console.error('Error al eliminar proyecto:', error);
      alert(error.response?.data?.error || 'Error al eliminar proyecto');
    }
  };

  const enviarProyectoEmail = async (proyecto_id, nombre_proyecto) => {
    if (!window.confirm(`¿Enviar reporte del proyecto "${nombre_proyecto}" por email al cliente?`)) return;

    try {
      await proyectoService.enviarEmail(proyecto_id);
      alert('Reporte del proyecto enviado por email exitosamente');
    } catch (error) {
      console.error('Error al enviar proyecto:', error);
      alert(error.response?.data?.error || 'Error al enviar proyecto por email');
    }
  };

  const crearDesdeCotizacion = async (cotizacion_id) => {
    const cotizacion = cotizaciones.find(cot => cot.id === cotizacion_id);
    const cliente = clientes.find(c => c.id === cotizacion?.cliente_id);

    if (!cliente || !cotizacion) return;

    // Confirmar antes de crear
    if (!window.confirm(`¿Crear proyecto desde la cotización "${cotizacion.titulo}"?\n\nCliente: ${cliente.nombre}\nTotal: $${parseFloat(cotizacion.total).toLocaleString()}`)) {
      return;
    }

    try {
      await proyectoService.createFromCotizacion({
        cotizacion_id,
        fecha_inicio: new Date().toISOString().split('T')[0],
        direccion: cliente.direccion || ''
      });
      cargarProyectos();
      cargarCotizaciones(); // Recargar cotizaciones para actualizar la lista
      alert('Proyecto creado desde cotización');
    } catch (error) {
      console.error('Error al crear proyecto:', error);
      alert(error.response?.data?.error || 'Error al crear proyecto');
    }
  };

  if (loading) {
    return <div className="loading">Cargando proyectos...</div>;
  }

  return (
    <div className="proyectos-container">
      <div className="page-header">
        <h1>{esCapataz ? 'Mis Proyectos' : 'Proyectos'}</h1>
        {esContratista && (
          <button className="btn btn-primary" onClick={abrirModal}>
            + Nuevo Proyecto
          </button>
        )}
      </div>

      {esContratista && (
        <div className="filtros">
          <select
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value)}
            className="filtro-estado"
          >
            <option value="">Todos los estados</option>
            <option value="planificacion">Planificación</option>
            <option value="en_curso">En Curso</option>
            <option value="pausado">Pausado</option>
            <option value="completado">Completado</option>
            <option value="cancelado">Cancelado</option>
          </select>
        </div>
      )}

      {proyectos.length === 0 ? (
        <div className="empty-state">
          <p>{esCapataz ? 'No tienes proyectos asignados' : 'No hay proyectos'}</p>
          {esContratista && (
            <button className="btn btn-primary" onClick={abrirModal}>
              Crear primer proyecto
            </button>
          )}
        </div>
      ) : (
        <div className="proyectos-grid">
          {proyectos.map(proyecto => (
            <div key={proyecto.id} className="proyecto-card">
              <div className="proyecto-header">
                <div>
                  <h3>{proyecto.nombre}</h3>
                  <p className="proyecto-numero">{proyecto.numero_proyecto}</p>
                </div>
                <span className={`estado-badge ${proyecto.estado}`}>
                  {proyecto.estado.replace('_', ' ')}
                </span>
              </div>

              <div className="proyecto-info">
                <p><strong>Cliente:</strong> {proyecto.cliente_nombre}</p>
                {proyecto.capataz_nombre && (
                  <p><strong>Capataz:</strong> {proyecto.capataz_nombre}</p>
                )}
                {proyecto.direccion && (
                  <p><strong>Dirección:</strong> {proyecto.direccion}</p>
                )}
                {proyecto.fecha_inicio && (
                  <p><strong>Inicio:</strong> {new Date(proyecto.fecha_inicio).toLocaleDateString()}</p>
                )}
                {proyecto.presupuesto_asignado && (
                  <p><strong>Presupuesto:</strong> ${parseFloat(proyecto.presupuesto_asignado).toLocaleString()}</p>
                )}
              </div>

              <div className="proyecto-progreso">
                <div className="progreso-header">
                  <span>Avance</span>
                  <span className="progreso-porcentaje">{proyecto.porcentaje_avance || 0}%</span>
                </div>
                <div className="progreso-bar">
                  <div
                    className="progreso-fill"
                    style={{ width: `${proyecto.porcentaje_avance || 0}%` }}
                  ></div>
                </div>
              </div>

              <div className="proyecto-actions">
                <button
                  className="btn btn-secondary"
                  onClick={() => navigate(`/proyectos/${proyecto.id}`)}
                >
                  Ver Detalles
                </button>

                {esContratista && (
                  <button
                    className="btn btn-primary"
                    onClick={() => enviarProyectoEmail(proyecto.id, proyecto.nombre)}
                    title="Enviar reporte del proyecto por email"
                  >
                    📧 Enviar Email
                  </button>
                )}

                {esContratista && proyecto.estado === 'planificacion' && (
                  <>
                    <button
                      className="btn btn-success"
                      onClick={() => aprobarProyecto(proyecto.id)}
                    >
                      ✓ Aprobar Proyecto
                    </button>
                    <button
                      className="btn btn-danger"
                      onClick={() => eliminarProyecto(proyecto.id, proyecto.nombre)}
                    >
                      🗑️ Eliminar
                    </button>
                  </>
                )}

                {esCapataz && proyecto.estado === 'en_curso' && (
                  <button
                    className="btn btn-primary"
                    onClick={() => abrirModalAvance(proyecto)}
                  >
                    Registrar Avance
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal Crear Proyecto */}
      {modalAbierto && esContratista && (
        <div className="modal-overlay" onClick={cerrarModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Nuevo Proyecto</h2>

            {cotizaciones.length > 0 && (
              <div className="cotizaciones-disponibles">
                <h3>O crear desde cotización aprobada:</h3>
                <select
                  onChange={(e) => {
                    if (e.target.value) {
                      crearDesdeCotizacion(parseInt(e.target.value));
                      cerrarModal();
                    }
                  }}
                  defaultValue=""
                >
                  <option value="">Seleccionar cotización</option>
                  {cotizaciones.map(cot => (
                    <option key={cot.id} value={cot.id}>
                      {cot.numero_cotizacion} - {cot.titulo} (${cot.total})
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="separator">
              <span>O crear proyecto manual</span>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Cliente *</label>
                <select
                  name="cliente_id"
                  value={formData.cliente_id}
                  onChange={handleChange}
                  required
                >
                  <option value="">Seleccionar cliente</option>
                  {clientes.map(cliente => (
                    <option key={cliente.id} value={cliente.id}>
                      {cliente.nombre}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Nombre del Proyecto *</label>
                <input
                  type="text"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleChange}
                  required
                  placeholder="Nombre del proyecto"
                />
              </div>

              <div className="form-group">
                <label>Descripción</label>
                <textarea
                  name="descripcion"
                  value={formData.descripcion}
                  onChange={handleChange}
                  rows="3"
                  placeholder="Descripción del proyecto"
                />
              </div>

              <div className="form-group">
                <label>Dirección</label>
                <input
                  type="text"
                  name="direccion"
                  value={formData.direccion}
                  onChange={handleChange}
                  placeholder="Dirección del proyecto"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Fecha de Inicio</label>
                  <input
                    type="date"
                    name="fecha_inicio"
                    value={formData.fecha_inicio}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label>Fecha Fin Estimada</label>
                  <input
                    type="date"
                    name="fecha_fin_estimada"
                    value={formData.fecha_fin_estimada}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Presupuesto Asignado</label>
                <input
                  type="number"
                  name="presupuesto_asignado"
                  value={formData.presupuesto_asignado}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                />
              </div>

              <div className="form-group">
                <label>Observaciones</label>
                <textarea
                  name="observaciones"
                  value={formData.observaciones}
                  onChange={handleChange}
                  rows="3"
                  placeholder="Observaciones adicionales del proyecto..."
                />
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={cerrarModal}>
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary">
                  Crear Proyecto
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Registrar Avance */}
      {modalAvance && esCapataz && (
        <div className="modal-overlay" onClick={cerrarModalAvance}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Registrar Avance</h2>
            <p className="proyecto-nombre-modal">{proyectoSeleccionado?.nombre}</p>

            <form onSubmit={handleSubmitAvance}>
              <div className="form-group">
                <label>Fecha *</label>
                <input
                  type="date"
                  name="fecha"
                  value={avanceData.fecha}
                  onChange={handleAvanceChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>Descripción del Avance *</label>
                <textarea
                  name="descripcion"
                  value={avanceData.descripcion}
                  onChange={handleAvanceChange}
                  required
                  rows="4"
                  placeholder="Describe el trabajo realizado hoy..."
                />
              </div>

              <div className="form-group">
                <label>Porcentaje de Avance Total del Proyecto</label>
                <input
                  type="number"
                  name="porcentaje_avance"
                  value={avanceData.porcentaje_avance}
                  onChange={handleAvanceChange}
                  min="0"
                  max="100"
                  placeholder="0-100"
                />
                <small>Actualiza el avance general del proyecto</small>
              </div>

              <div className="form-group">
                <label>Observaciones</label>
                <textarea
                  name="observaciones"
                  value={avanceData.observaciones}
                  onChange={handleAvanceChange}
                  rows="2"
                  placeholder="Observaciones adicionales..."
                />
              </div>

              <div className="form-group">
                <label>Incidencias</label>
                <textarea
                  name="incidencias"
                  value={avanceData.incidencias}
                  onChange={handleAvanceChange}
                  rows="2"
                  placeholder="Reporta cualquier problema o incidencia..."
                />
              </div>

              <div className="form-group">
                <label>Fotos del Avance</label>
                <ImageUploader
                  onImageUploaded={handleImagenesAvance}
                  multiple={true}
                />
                {avanceData.imagenes.length > 0 && (
                  <p className="imagenes-count">
                    {avanceData.imagenes.length} foto(s) seleccionada(s)
                  </p>
                )}
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={cerrarModalAvance}>
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary">
                  Registrar Avance
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Proyectos;
